// Copyright (c) 2021 WiseTime. All rights reserved.

import { EMPTY, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, finalize, map, mergeMap, withLatestFrom } from "rxjs/operators"
import * as Actions from "../actions"
import * as Slice from "../slice"
import { GetActivityTypeRequest } from "generated/legebill/site/activitytype/v4/activity_type_pb"
import { grpc } from "app/rpc/executor"
import { activityTypeServiceClient } from "app/rpc/client"
import { t } from "@lingui/macro"
import { ActivityType } from "../../../generated/legebill/type/activity_type_pb"
import { isGrpcError } from "../../../lib/rpcutils"
import { StatusCode } from "grpc-web"
import { showSnackBar } from "../../snackbar/actions"
import { RootState } from "../../../app/store"

type GetActivityTypeRpc = (request: GetActivityTypeRequest) => Observable<ActivityType>

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Set<string>()

// Export for testing
export const __test__ = { inFlightRequests }

export const getActivityType = (getActivityTypeRpc: GetActivityTypeRpc) =>
  (action$: Observable<Action>, state$: Observable<RootState>): Observable<Action> =>
    action$.pipe(
      filter(Actions.getActivityType.match),
      withLatestFrom(state$),
      mergeMap(([{ payload: request }, state]) => {
        const activityTypeId = request.getId()
        // Skip if activity type already in cache or request is already in flight
        if (state.activityTypes.activityTypesCache?.[activityTypeId] || inFlightRequests.has(activityTypeId)) {
          return EMPTY
        }

        // Mark as in flight
        inFlightRequests.add(activityTypeId)

        return of(request)
          .pipe(
            mergeMap(getActivityTypeRpc),
            map(t => t.toObject()),
            map(response => Slice.addActivityTypeToCache(response)),
            catchError((e: unknown) => {
              if (isGrpcError(e)) {
                const { code } = e
                if (code === StatusCode.NOT_FOUND) {
                  return of(showSnackBar({
                    message: t({ id: "msg_get_activity_type_not_found", message: "Activity type not found." }),
                    messageType: "ERROR",
                  }))
                }

                if (code === StatusCode.PERMISSION_DENIED) {
                  return of(showSnackBar({
                    message: t({
                      id: "msg_get_activity_type_permission_denied",
                      message: "Failed to get activity type. You do not have permission to access this activity type."
                    }),
                    messageType: "ERROR",
                  }))
                }

                if (code === StatusCode.INVALID_ARGUMENT) {
                  return of(showSnackBar({
                    message: t({
                      id: "msg_get_activity_type_failed",
                      message: "Failed to get activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
                    }),
                    messageType: "ERROR",
                  }))
                }
              }
              return of(showSnackBar({
                message: t({
                  id: "msg_get_activity_type_failed",
                  message: "Failed to get activity type. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com."
                }),
                messageType: "ERROR",
              }))
            }),
            // Clean up in-flight tracking on completion or error
            finalize(() => inFlightRequests.delete(activityTypeId))
          )
      })
    )

const getActivityTypeRpc = (request: GetActivityTypeRequest): Observable<ActivityType> =>
  grpc(() => activityTypeServiceClient.getActivityType(request, null))

export default getActivityType(getActivityTypeRpc)
